import { notification } from "antd";

type BlobResponse = {
  data: Blob;
  headers: Record<string, string | undefined>;
};

class BlobExportResponseError extends Error {
  blob: Blob;

  constructor(message: string, blob: Blob) {
    super(message);
    this.name = "BlobExportResponseError";
    this.blob = blob;
  }
}

export const parseDownloadFilename = (
  disposition: string | undefined,
  fallbackFilename: string,
) => {
  if (!disposition) return fallbackFilename;

  const match = disposition.match(
    /filename\*=UTF-8''(.+)|filename="?([^"]+)"?/,
  );

  return match ? decodeURIComponent(match[1] || match[2]) : fallbackFilename;
};

export const extractBlobErrorMessage = async (
  blob: Blob,
  fallbackMessage: string,
) => {
  const text = await blob.text().catch(() => "");

  try {
    const payload = JSON.parse(text) as { message?: string };
    return payload.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

export const downloadBlobResponse = async (
  response: BlobResponse,
  fallbackFilename: string,
  fallbackErrorMessage = "Export lỗi",
) => {
  const contentType =
    response.headers["content-type"] || "application/octet-stream";
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });

  if (contentType.includes("application/json")) {
    const message = await extractBlobErrorMessage(blob, fallbackErrorMessage);
    throw new BlobExportResponseError(message, blob);
  }

  const filename = parseDownloadFilename(
    response.headers["content-disposition"],
    fallbackFilename,
  );
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);

  return true;
};

export const handleBlobExportError = async (
  error: unknown,
  fallbackMessage = "Export lỗi",
) => {
  const responseBlob = (
    error as { response?: { data?: unknown } } | null | undefined
  )?.response?.data;
  const blob =
    error instanceof BlobExportResponseError
      ? error.blob
      : responseBlob instanceof Blob
        ? responseBlob
        : undefined;

  if (!blob) return false;

  const message = await extractBlobErrorMessage(blob, fallbackMessage);
  notification.error({ message });
  return true;
};
